import { useDebounce } from "react-use";
import React, { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Space, Drawer, Select, Spin, Row, Col, DatePicker } from "antd";

import { openNotificationWithIcon } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { setIsRequeryMultipleBatchDrawerOpen } from "pages/Generation/generationSlice";
import {
  useRequeryBatchesFinalStatusMutation,
  useLazyGetBatchesToUpdateFinalStatusQuery,
} from "pages/Generation/generationApiSlice";

const { RangePicker } = DatePicker;

const RequeryMultipleBatchDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [endTransmittedDate, setEndTransmittedDate] = useState("");
  const [selectedBatches, setSelectedBatches] = useState<any>([]);
  const [startTransmittedDate, setStartTransmittedDate] = useState("");
  const [searchValue, setSearchValue] = useState<any>({ key: "", value: "" });
  const { isRequeryMultipleBatchDrawerOpen } = useAppSelector((state) => state.generation);

  const [getBatchesToUpdateFinalStatus, getBatchesToUpdateFinalStatusResult] =
    useLazyGetBatchesToUpdateFinalStatusQuery();
  const [requeryMultipleBatches, requeryMultipleBatchesResult] =
    useRequeryBatchesFinalStatusMutation();

  const closeDrawer = () => {
    dispatch(setIsRequeryMultipleBatchDrawerOpen(false));
  };
  const [isReady, cancel] = useDebounce(
    () => getBatchesToUpdateFinalStatus({ searchParam: searchValue.value }),
    800,
    [searchValue.key],
  );

  const handleRequeryMultipleBatches = () => {
    requeryMultipleBatches({
      batchIds: selectedBatches,
      startTransmittedDate,
      endTransmittedDate,
    })
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("Successfully requeried", payload.response, "success");
        closeDrawer();
        setSelectedBatches([]);
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          if (error.data?.fieldErrorList?.length) {
            error.data?.fieldErrorList?.forEach((errorMessageObj: any) =>
              openNotificationWithIcon(
                error.data?.errorMessage,
                errorMessageObj?.errorMessage,
                "warning",
              ),
            );
          } else {
            openNotificationWithIcon(error.status, error.data, "error");
          }
        } else if (error.status === 400) {
          if (error.data?.fieldErrorList?.length) {
            error.data?.fieldErrorList?.forEach((errorMessageObj: any) =>
              openNotificationWithIcon(
                error.data?.errorMessage,
                errorMessageObj?.errorMessage,
                "error",
              ),
            );
          } else {
            openNotificationWithIcon(error.status, error.data, "warning");
          }
        }
      })
      .finally(() => {});
  };

  return (
    <Drawer
      width={500}
      closable={false}
      placement="right"
      maskClosable={false}
      destroyOnClose={true}
      onClose={() => {
        closeDrawer();
        setSelectedBatches([]);
      }}
      title="Holiday: Requery multiple batches"
      open={isRequeryMultipleBatchDrawerOpen}
      extra={
        <Space>
          <Button
            type="text"
            onClick={() => {
              closeDrawer();
              setSelectedBatches([]);
            }}
          >
            <CloseOutlined />
          </Button>
        </Space>
      }
    >
      <Row>
        <Col span={24} style={{ marginBottom: 15 }}>
          <Select
            allowClear
            labelInValue
            mode="multiple"
            filterOption={false}
            style={{ width: "100%" }}
            placeholder="Select batches"
            onClear={() => setSelectedBatches([])}
            disabled={requeryMultipleBatchesResult.isLoading}
            onSearch={(value) => {
              setSearchValue({ key: value, value: value });
            }}
            onSelect={({ value }) =>
              setSelectedBatches((prevBatches: string[]) => [...prevBatches, value])
            }
            onDeselect={({ value }) =>
              setSelectedBatches((prevBatches: string[]) =>
                prevBatches.filter((batch) => batch !== value),
              )
            }
            notFoundContent={
              getBatchesToUpdateFinalStatusResult.isFetching && isReady() ? (
                <Spin size="small" />
              ) : null
            }
            options={
              getBatchesToUpdateFinalStatusResult?.currentData?.response
                ? getBatchesToUpdateFinalStatusResult?.currentData?.response?.map((value) => ({
                    key: value,
                    value: value,
                  }))
                : []
            }
          />
        </Col>

        <Col span={24} style={{ marginBottom: 25 }}>
          <Row justify="space-between">
            <Col>
              <RangePicker
                format="YYYY-MM-DD"
                allowClear
                onChange={(_, dateString) => {
                  setStartTransmittedDate(() => dateString[0]);
                  setEndTransmittedDate(() => dateString[1]);
                }}
              />
            </Col>

            <Col>
              <Button
                type="primary"
                loading={requeryMultipleBatchesResult.isLoading}
                onClick={() => {
                  cancel();
                  handleRequeryMultipleBatches();
                }}
              >
                Requery
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Drawer>
  );
};

export default RequeryMultipleBatchDrawer;
